import * as THREE from 'three'
import {
  VillagerJob, VILLAGER_COLORS,
  BODY, LEG_BL, LEG_BR, LEG_FL, LEG_FR, HEAD, TAIL, ARM_L, ARM_R,
} from './types'

// ─── Geometry builder (same pattern as VillageRenderer) ─────────────
interface GeoArrays {
  positions: number[]
  colors: number[]
  bodyParts: number[]
  indices: number[]
}

function createGeo(): GeoArrays {
  return { positions: [], colors: [], bodyParts: [], indices: [] }
}

function addBox(
  g: GeoArrays,
  cx: number, cy: number, cz: number,
  hw: number, hh: number, hd: number,
  color: [number, number, number],
  bodyPart: number = BODY,
) {
  const b = g.positions.length / 3
  for (const sx of [-1, 1]) {
    for (const sy of [-1, 1]) {
      for (const sz of [-1, 1]) {
        g.positions.push(cx + hw * sx, cy + hh * sy, cz + hd * sz)
        g.colors.push(color[0], color[1], color[2])
        g.bodyParts.push(bodyPart)
      }
    }
  }
  // 6 faces, 2 triangles each
  const faces = [
    [0, 1, 2, 3], [4, 6, 5, 7], // ±X
    [0, 4, 1, 5], [2, 3, 6, 7], // ±Y
    [0, 2, 4, 6], [1, 5, 3, 7], // ±Z
  ]
  for (const [a, bb, c, d] of faces) {
    g.indices.push(b + a, b + bb, b + c, b + bb, b + d, b + c)
  }
}

function addPrism(
  g: GeoArrays,
  cx: number, cy: number, cz: number,
  radius: number, halfHeight: number, sides: number,
  color: [number, number, number],
  bodyPart: number = BODY,
) {
  const b = g.positions.length / 3
  // Bottom ring then top ring
  for (let ring = 0; ring < 2; ring++) {
    const yOff = ring === 0 ? -halfHeight : halfHeight
    for (let i = 0; i < sides; i++) {
      const angle = (i / sides) * Math.PI * 2
      g.positions.push(cx + Math.cos(angle) * radius, cy + yOff, cz + Math.sin(angle) * radius)
      g.colors.push(color[0], color[1], color[2])
      g.bodyParts.push(bodyPart)
    }
  }
  // Side faces
  for (let i = 0; i < sides; i++) {
    const i2 = (i + 1) % sides
    g.indices.push(b + i, b + sides + ((i + 1) % sides), b + i2, b + i, b + sides + i, b + sides + i2)
  }
  // Top cap
  const topC = g.positions.length / 3
  g.positions.push(cx, cy + halfHeight, cz)
  g.colors.push(color[0], color[1], color[2])
  g.bodyParts.push(bodyPart)
  for (let i = 0; i < sides; i++) {
    g.indices.push(topC, b + sides + (i + 1) % sides, b + sides + i)
  }
  // Bottom cap
  const botC = g.positions.length / 3
  g.positions.push(cx, cy - halfHeight, cz)
  g.colors.push(color[0], color[1], color[2])
  g.bodyParts.push(bodyPart)
  for (let i = 0; i < sides; i++) {
    g.indices.push(botC, b + i, b + (i + 1) % sides)
  }
}

function toBufferGeometry(g: GeoArrays): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.Float32BufferAttribute(g.positions, 3))
  geo.setAttribute('color', new THREE.Float32BufferAttribute(g.colors, 3))
  geo.setAttribute('bodyPart', new THREE.Float32BufferAttribute(g.bodyParts, 1))
  geo.setIndex(g.indices)
  geo.computeVertexNormals()
  return geo
}

// ─── Horse ──────────────────────────────────────────────────────────
// Low-poly horse: muscular body, long neck, head with muzzle, 4 legs, tail. ~280 tris
// All geometry in local space: Y-up, facing +Z, origin at feet
export function buildHorseGeometry(variant: number = 0): THREE.BufferGeometry {
  const g = createGeo()

  // Color variants: bay, black, dapple gray
  const coatColors: [number, number, number][] = [
    [0.50, 0.30, 0.15],  // bay brown
    [0.18, 0.15, 0.13],  // black
    [0.65, 0.62, 0.58],  // dapple gray
  ]
  const maneColors: [number, number, number][] = [
    [0.12, 0.08, 0.05],  // dark mane
    [0.10, 0.08, 0.06],  // black mane
    [0.85, 0.82, 0.78],  // light mane
  ]
  const coat = coatColors[variant % 3]
  const mane = maneColors[variant % 3]
  const hoofColor: [number, number, number] = [0.15, 0.12, 0.10]
  const eyeColor: [number, number, number] = [0.10, 0.08, 0.06]
  const muzzle: [number, number, number] = [coat[0] * 0.75, coat[1] * 0.75, coat[2] * 0.75]

  // Main body (barrel)
  addBox(g, 0, 1.65, 0, 0.55, 0.50, 1.20, coat, BODY)
  // Chest (front, slightly wider)
  addBox(g, 0, 1.70, 0.85, 0.48, 0.42, 0.35, coat, BODY)
  // Rump (back, tapered)
  addBox(g, 0, 1.72, -1.0, 0.50, 0.45, 0.30, coat, BODY)

  // Neck (angled forward-up)
  addBox(g, 0, 2.20, 1.25, 0.28, 0.45, 0.30, coat, BODY)
  // Upper neck
  addBox(g, 0, 2.65, 1.45, 0.22, 0.28, 0.22, coat, HEAD)

  // Head
  addBox(g, 0, 2.75, 1.80, 0.20, 0.22, 0.30, coat, HEAD)
  // Muzzle (longer, narrower)
  addBox(g, 0, 2.62, 2.10, 0.14, 0.14, 0.18, muzzle, HEAD)

  // Ears
  addBox(g, -0.10, 2.98, 1.80, 0.04, 0.10, 0.04, coat, HEAD)
  addBox(g,  0.10, 2.98, 1.80, 0.04, 0.10, 0.04, coat, HEAD)

  // Eyes
  addBox(g, -0.20, 2.80, 1.82, 0.02, 0.04, 0.04, eyeColor, HEAD)
  addBox(g,  0.20, 2.80, 1.82, 0.02, 0.04, 0.04, eyeColor, HEAD)

  // Mane (ridge along neck + head)
  addBox(g, 0, 2.50, 1.15, 0.04, 0.12, 0.50, mane, HEAD)
  addBox(g, 0, 2.88, 1.60, 0.04, 0.08, 0.30, mane, HEAD)

  // Legs (4 legs, each with upper + lower + hoof)
  const legUpper = 0.55, legLower = 0.45
  const legW = 0.12
  // Front-left
  addBox(g, -0.30, 1.05, 0.70, legW, legUpper / 2, legW, coat, LEG_FL)
  addBox(g, -0.30, 0.40, 0.70, legW * 0.85, legLower / 2, legW * 0.85, coat, LEG_FL)
  addBox(g, -0.30, 0.08, 0.70, legW * 0.9, 0.08, legW * 1.1, hoofColor, LEG_FL)
  // Front-right
  addBox(g,  0.30, 1.05, 0.70, legW, legUpper / 2, legW, coat, LEG_FR)
  addBox(g,  0.30, 0.40, 0.70, legW * 0.85, legLower / 2, legW * 0.85, coat, LEG_FR)
  addBox(g,  0.30, 0.08, 0.70, legW * 0.9, 0.08, legW * 1.1, hoofColor, LEG_FR)
  // Back-left
  addBox(g, -0.32, 1.05, -0.80, legW, legUpper / 2, legW, coat, LEG_BL)
  addBox(g, -0.32, 0.40, -0.80, legW * 0.85, legLower / 2, legW * 0.85, coat, LEG_BL)
  addBox(g, -0.32, 0.08, -0.80, legW * 0.9, 0.08, legW * 1.1, hoofColor, LEG_BL)
  // Back-right
  addBox(g,  0.32, 1.05, -0.80, legW, legUpper / 2, legW, coat, LEG_BR)
  addBox(g,  0.32, 0.40, -0.80, legW * 0.85, legLower / 2, legW * 0.85, coat, LEG_BR)
  addBox(g,  0.32, 0.08, -0.80, legW * 0.9, 0.08, legW * 1.1, hoofColor, LEG_BR)

  // Tail
  addBox(g, 0, 1.90, -1.40, 0.06, 0.06, 0.30, mane, TAIL)
  addBox(g, 0, 1.70, -1.65, 0.05, 0.05, 0.20, mane, TAIL)

  return toBufferGeometry(g)
}

// ─── Wolf ───────────────────────────────────────────────────────────
// Low-poly wolf: lean body, narrow head, thick fur, 4 legs, bushy tail. ~260 tris
export function buildWolfGeometry(): THREE.BufferGeometry {
  const g = createGeo()

  const furColor: [number, number, number] = [0.38, 0.35, 0.30]
  const darkFur: [number, number, number] = [0.22, 0.20, 0.18]
  const bellyFur: [number, number, number] = [0.55, 0.50, 0.42]
  const eyeColor: [number, number, number] = [0.85, 0.65, 0.15]  // amber
  const noseColor: [number, number, number] = [0.10, 0.08, 0.06]
  const legColor: [number, number, number] = [0.32, 0.30, 0.26]

  // Main body
  addBox(g, 0, 1.05, 0, 0.40, 0.35, 0.90, furColor, BODY)
  // Belly (lighter underside)
  addBox(g, 0, 0.78, 0, 0.32, 0.10, 0.70, bellyFur, BODY)
  // Chest (front, broader)
  addBox(g, 0, 1.12, 0.65, 0.36, 0.30, 0.28, furColor, BODY)
  // Haunches (back, powerful)
  addBox(g, 0, 1.10, -0.70, 0.38, 0.32, 0.25, furColor, BODY)

  // Neck
  addBox(g, 0, 1.30, 0.90, 0.22, 0.22, 0.22, furColor, BODY)

  // Head
  addBox(g, 0, 1.35, 1.20, 0.22, 0.20, 0.25, furColor, HEAD)
  // Snout (narrow, pointed)
  addBox(g, 0, 1.28, 1.50, 0.12, 0.10, 0.18, darkFur, HEAD)
  // Nose
  addBox(g, 0, 1.30, 1.68, 0.05, 0.04, 0.03, noseColor, HEAD)

  // Ears (pointed, upright)
  addBox(g, -0.12, 1.58, 1.18, 0.05, 0.10, 0.06, darkFur, HEAD)
  addBox(g,  0.12, 1.58, 1.18, 0.05, 0.10, 0.06, darkFur, HEAD)

  // Eyes (amber)
  addBox(g, -0.18, 1.40, 1.30, 0.03, 0.035, 0.03, eyeColor, HEAD)
  addBox(g,  0.18, 1.40, 1.30, 0.03, 0.035, 0.03, eyeColor, HEAD)

  // Legs
  const legW = 0.09
  // Front-left
  addBox(g, -0.22, 0.55, 0.55, legW, 0.28, legW, legColor, LEG_FL)
  addBox(g, -0.22, 0.15, 0.55, legW * 0.8, 0.15, legW * 0.8, legColor, LEG_FL)
  // Front-right
  addBox(g,  0.22, 0.55, 0.55, legW, 0.28, legW, legColor, LEG_FR)
  addBox(g,  0.22, 0.15, 0.55, legW * 0.8, 0.15, legW * 0.8, legColor, LEG_FR)
  // Back-left
  addBox(g, -0.24, 0.55, -0.60, legW, 0.28, legW, legColor, LEG_BL)
  addBox(g, -0.24, 0.15, -0.60, legW * 0.8, 0.15, legW * 0.8, legColor, LEG_BL)
  // Back-right
  addBox(g,  0.24, 0.55, -0.60, legW, 0.28, legW, legColor, LEG_BR)
  addBox(g,  0.24, 0.15, -0.60, legW * 0.8, 0.15, legW * 0.8, legColor, LEG_BR)

  // Bushy tail (raised, curved)
  addBox(g, 0, 1.35, -1.05, 0.10, 0.10, 0.25, furColor, TAIL)
  addBox(g, 0, 1.50, -1.20, 0.08, 0.08, 0.15, darkFur, TAIL)
  // White tail tip
  addBox(g, 0, 1.55, -1.32, 0.06, 0.06, 0.08, bellyFur, TAIL)

  return toBufferGeometry(g)
}

// ─── Sheep ──────────────────────────────────────────────────────────
// Low-poly sheep: woolly body, dark head, 4 thin legs. ~200 tris
// All geometry in local space: Y-up, facing +Z, origin at feet
export function buildSheepGeometry(): THREE.BufferGeometry {
  const g = createGeo()

  const woolColor: [number, number, number] = [0.92, 0.89, 0.83]
  const darkColor: [number, number, number] = [0.20, 0.17, 0.14]
  const legColor: [number, number, number]  = [0.22, 0.19, 0.15]

  // Body (main wool mass)
  addBox(g, 0, 1.1, 0, 0.75, 0.55, 1.0, woolColor, BODY)

  // Head
  addBox(g, 0, 1.25, 1.2, 0.3, 0.3, 0.35, darkColor, HEAD)

  // Legs (4 thin boxes)
  const legHW = 0.1, legHH = 0.35, legHD = 0.1
  addBox(g, -0.45, 0.35, -0.6, legHW, legHH, legHD, legColor, LEG_BL) // back-left
  addBox(g,  0.45, 0.35, -0.6, legHW, legHH, legHD, legColor, LEG_BR) // back-right
  addBox(g, -0.45, 0.35,  0.6, legHW, legHH, legHD, legColor, LEG_FL) // front-left
  addBox(g,  0.45, 0.35,  0.6, legHW, legHH, legHD, legColor, LEG_FR) // front-right

  // Tail (tiny nub)
  addBox(g, 0, 1.3, -1.05, 0.12, 0.12, 0.1, woolColor, TAIL)

  return toBufferGeometry(g)
}

// ─── Chicken ────────────────────────────────────────────────────────
// Low-poly chicken: round body, small head, beak, tail fan, 2 legs. ~180 tris
export function buildChickenGeometry(): THREE.BufferGeometry {
  const g = createGeo()

  const bodyColor: [number, number, number] = [0.80, 0.55, 0.35] // warm brown
  const headColor: [number, number, number] = [0.82, 0.60, 0.38]
  const beakColor: [number, number, number] = [0.90, 0.70, 0.25]
  const combColor: [number, number, number] = [0.85, 0.20, 0.15]
  const legColor: [number, number, number]  = [0.85, 0.68, 0.28]
  const tailColor: [number, number, number] = [0.30, 0.25, 0.20]

  // Body (rounded via octagonal prism)
  addPrism(g, 0, 0.55, 0, 0.35, 0.3, 8, bodyColor, BODY)

  // Head
  addBox(g, 0, 0.95, 0.35, 0.18, 0.18, 0.18, headColor, HEAD)

  // Beak
  addBox(g, 0, 0.90, 0.58, 0.06, 0.04, 0.08, beakColor, HEAD)

  // Comb (on top of head)
  addBox(g, 0, 1.15, 0.35, 0.04, 0.06, 0.1, combColor, HEAD)

  // Legs (2 thin) — chickens only have 2 legs, use FL/FR
  addBox(g, -0.12, 0.15, 0.05, 0.04, 0.15, 0.04, legColor, LEG_FL)
  addBox(g,  0.12, 0.15, 0.05, 0.04, 0.15, 0.04, legColor, LEG_FR)

  // Tail feathers (angled up and back)
  addBox(g, 0, 0.75, -0.45, 0.15, 0.2, 0.08, tailColor, TAIL)

  return toBufferGeometry(g)
}

// ─── Villager ───────────────────────────────────────────────────────
// Low-poly medieval villager with tapered torso, proper proportions,
// facial features, and job-specific accessories. ~350 tris
export function buildVillagerGeometry(job: VillagerJob = VillagerJob.Farmer): THREE.BufferGeometry {
  const g = createGeo()
  const colors = VILLAGER_COLORS[job] ?? VILLAGER_COLORS[VillagerJob.Farmer]

  const { tunic, pants, skin, hair } = colors
  const eyeColor: [number, number, number] = [0.12, 0.10, 0.08]
  const bootColor: [number, number, number] = [0.22, 0.18, 0.14]
  const beltColor: [number, number, number] = [tunic[0] * 0.5, tunic[1] * 0.5, tunic[2] * 0.5]
  const noseSkin: [number, number, number] = [skin[0] * 0.92, skin[1] * 0.92, skin[2] * 0.92]

  // ─── Torso (tapered: wider at shoulders, narrower at waist) ───
  addBox(g, 0, 1.68, 0, 0.42, 0.26, 0.22, tunic, BODY)  // upper chest
  addBox(g, 0, 1.32, 0, 0.34, 0.18, 0.18, tunic, BODY)   // waist

  // Belt
  addBox(g, 0, 1.16, 0, 0.36, 0.04, 0.20, beltColor, BODY)

  // Neck
  addPrism(g, 0, 2.00, 0, 0.10, 0.06, 6, skin, BODY)

  // ─── Head ───
  addPrism(g, 0, 2.30, 0.02, 0.22, 0.24, 8, skin, HEAD)

  // Hair (top cap + sideburns)
  addBox(g, 0, 2.60, -0.02, 0.24, 0.08, 0.24, hair, HEAD)
  addBox(g, -0.22, 2.36, -0.06, 0.04, 0.14, 0.16, hair, HEAD)
  addBox(g,  0.22, 2.36, -0.06, 0.04, 0.14, 0.16, hair, HEAD)
  // Hair back
  addBox(g, 0, 2.36, -0.20, 0.20, 0.14, 0.04, hair, HEAD)

  // Eyes
  addBox(g, -0.09, 2.34, 0.22, 0.04, 0.025, 0.02, eyeColor, HEAD)
  addBox(g,  0.09, 2.34, 0.22, 0.04, 0.025, 0.02, eyeColor, HEAD)

  // Nose
  addBox(g, 0, 2.24, 0.24, 0.025, 0.04, 0.04, noseSkin, HEAD)

  // ─── Arms (shoulder + upper arm + forearm + hand) ───
  // Shoulders
  addBox(g, -0.52, 1.80, 0, 0.12, 0.10, 0.14, tunic, ARM_L)
  addBox(g,  0.52, 1.80, 0, 0.12, 0.10, 0.14, tunic, ARM_R)
  // Upper arms
  addBox(g, -0.54, 1.56, 0, 0.10, 0.18, 0.10, tunic, ARM_L)
  addBox(g,  0.54, 1.56, 0, 0.10, 0.18, 0.10, tunic, ARM_R)
  // Forearms (rolled sleeves — skin visible)
  addBox(g, -0.54, 1.28, 0, 0.08, 0.14, 0.08, skin, ARM_L)
  addBox(g,  0.54, 1.28, 0, 0.08, 0.14, 0.08, skin, ARM_R)
  // Hands
  addBox(g, -0.54, 1.06, 0, 0.07, 0.07, 0.07, skin, ARM_L)
  addBox(g,  0.54, 1.06, 0, 0.07, 0.07, 0.07, skin, ARM_R)

  // ─── Legs ───
  addBox(g, -0.17, 0.58, 0, 0.14, 0.42, 0.14, pants, LEG_FL)
  addBox(g,  0.17, 0.58, 0, 0.14, 0.42, 0.14, pants, LEG_FR)

  // ─── Boots (taller, slightly forward for toe shape) ───
  addBox(g, -0.17, 0.10, 0.03, 0.15, 0.10, 0.20, bootColor, LEG_FL)
  addBox(g,  0.17, 0.10, 0.03, 0.15, 0.10, 0.20, bootColor, LEG_FR)

  // ─── Job-specific accessories ───
  if (job === VillagerJob.Guard) {
    // Iron helmet (replaces hair visually)
    addBox(g, 0, 2.64, 0, 0.26, 0.12, 0.26, [0.50, 0.50, 0.52], HEAD)
    addBox(g, 0, 2.52, 0, 0.28, 0.03, 0.28, [0.45, 0.45, 0.47], HEAD)
    // Nose guard
    addBox(g, 0, 2.38, 0.24, 0.02, 0.12, 0.02, [0.48, 0.48, 0.50], HEAD)
  }

  if (job === VillagerJob.Blacksmith) {
    // Leather apron over front of torso
    addBox(g, 0, 1.20, 0.16, 0.28, 0.36, 0.03, [0.35, 0.25, 0.15], BODY)
  }

  if (job === VillagerJob.Priest) {
    // Wide collar / stole
    addBox(g, 0, 1.92, 0, 0.46, 0.05, 0.26, [tunic[0] * 1.1, tunic[1] * 1.1, tunic[2] * 1.1], BODY)
  }

  if (job === VillagerJob.Merchant) {
    // Sash across chest
    addBox(g, 0.08, 1.60, 0.18, 0.28, 0.04, 0.03, [0.72, 0.58, 0.20], BODY)
  }

  return toBufferGeometry(g)
}
